import { styled, css } from 'styled-components';
import { rgba } from 'polished';
import { Props } from '.';

export const Container = styled.div<{
  $isDraggable: boolean;
  $isDragging: boolean;
  $type: Props['type'];
  $isDisabled?: boolean;
  $isPaused?: boolean;
  $showCheckboxAlways?: boolean;
}>`
  ${({ theme: { colors, motionSpeeds, motions }, $isDraggable, $isDragging, $type, $isDisabled, $isPaused, $showCheckboxAlways }) => css`
    align-items: center;
    background: ${colors.brandDarkTint95};
    border-radius: ${$type === 'compact' ? '0.25rem' : '0.5rem'};
    box-sizing: border-box;
    cursor: ${$isDraggable ? 'grab' : 'pointer'};
    display: flex;
    flex: 1;
    height: ${$isDragging ? '5.125rem' : 'auto'};
    overflow: hidden;
    position: relative;
    transition: all 0.2s ease;

    * {
      user-select: none;
    }

    .ds-tooltip-wrapper {
      line-height: 1em;

      ${
        $type !== 'compact' &&
        css`
          place-self: flex-start;
        `
      }
    }

    .ds-checkbox {
      margin-right: 0;
      opacity: ${$showCheckboxAlways ? 1 : 0};
      transition:
        margin-right ${motionSpeeds.fast} ${motions.default},
        opacity ${motionSpeeds.fast} ${motions.default},
        width ${motionSpeeds.fast} ${motions.default};
      width: ${$showCheckboxAlways ? '1.5rem' : 0};
    }

    &:hover .ds-checkbox,
    &:focus-within .ds-checkbox {
      margin-right: 0.25rem;
      opacity: 1;
      width: 1.5rem;
    }

    ${
      $isDraggable &&
      css`
        &:hover {
          box-shadow:
            0 0 0 1px ${colors.brandDarkTint90},
            0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.07),
            0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.1);
        }
      `
    }

    ${
      $type === 'daily' &&
      css`
        background: ${colors.brandLight};
        border-radius: 0.25rem;
        flex: 1;
        gap: 1rem;
        box-shadow: none;
        padding: 0.25rem;

        .ds-tooltip-wrapper {
          place-self: unset;
        }

        &:hover {
          background: ${rgba(colors.brandDarkTint95, 0.5)};
        }
      `
    }

    ${
      $type === 'preview' &&
      css`
        height: 4.875rem;
      `
    }

    ${
      $isPaused &&
      css`
        opacity: 0.7;
        filter: grayscale(1);
      `
    }

    ${
      $isDisabled &&
      css`
        cursor: not-allowed;
        opacity: 0.7;

        * {
          cursor: not-allowed;
        }

        &:hover {
          box-shadow: unset;
        }
      `
    }

    @media print {
      ${
        $type === 'weekly' &&
        css`
          border-bottom: 1px solid ${colors.brandDarkTint90};
          border-radius: 0;
          padding-bottom: 0.25rem;
        `
      }
    }
  `}
`;

export const ThumbArea = styled.div<{ $isLoading: boolean; $type: Props['type'] }>`
  ${({ $isLoading, $type }) => css`
    flex-shrink: 0;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 3rem;

    img,
    canvas {
      height: 100%;
      object-fit: cover;
      opacity: ${$isLoading ? 0 : 1};
      pointer-events: none;
      width: 100%;
    }

    .skeleton {
      border-radius: 0;
    }

    ${
      $type === 'daily' &&
      css`
        border-radius: 0.25rem;
        height: 2rem;
        width: 2rem;
      `
    }

    ${
      $type !== 'daily' &&
      css`
        border-radius: 0 0.5rem 0.5rem 0;
        order: 1;
      `
    }

    ${
      $type === 'preview' &&
      css`
        height: 4.875rem;
        width: 4.875rem;
      `
    }
  `}
`;

export const ThumbHighlight = styled.div`
  ${({ theme: { colors, fontWeights } }) => css`
    background: ${`linear-gradient(0deg, ${colors.brandDarkAlpha70} 0%, rgba(0, 0, 0, 0) 100%)`};
    bottom: 0;
    align-items: flex-end;
    display: flex;
    justify-content: flex-end;
    height: 100%;
    left: 0;
    padding: 0.5rem;
    position: absolute;
    width: 100%;
    z-index: 1;

    .icon {
      height: 1rem;
      width: 1rem;
    }

    span {
      color: ${colors.brandLight};
      font-size: 0.675rem !important;
      font-weight: ${fontWeights.medium};
      line-height: 1.3em;
      margin: 0;
      padding: 0;
    }
  `}
`;

export const ContentArea = styled.div<{ $type: Props['type'] }>`
  ${({ $type }) => css`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    padding: 0.5rem;
    z-index: 1;

    ${
      $type === 'daily' &&
      css`
        flex-direction: row;
        padding: 0;
      `
    }

    ${
      $type === 'compact' &&
      css`
        flex-direction: row;
        padding: 0.15rem 0.5rem;
      `
    }
  `}
`;

export const ClampTooltip = styled.div`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
`;

export const InfoArea = styled.div<{ $type: Props['type'] }>`
  ${({ theme: { colors, fontSizes }, $type }) => css`
    align-items: center;
    display: flex;
    gap: 0.25rem;
    place-self: flex-start;
    position: relative;
    text-transform: uppercase;
    z-index: 1;

    .ds-tooltip {
      display: flex;
    }

    p {
      color: ${colors.brandDarkTint30};
      display: -webkit-box;
      font-size: ${fontSizes.s12};
      line-break: anywhere;
      line-height: 1em;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    .icon,
    .spinner {
      flex-shrink: 0;
      height: 1rem;
      width: 1rem;
    }

    .info-area-icon-slot {
      align-items: center;
      display: inline-flex;
      flex-shrink: 0;
      height: 1rem;
    }

    .info-area-status-icon {
      align-items: center;
      display: inline-flex;
      flex-shrink: 0;
      height: 1rem;
      justify-content: center;
      position: relative;
      width: 1rem;
    }

    .ds-checkbox {
      flex-shrink: 0;
      overflow: hidden;
    }

    ${
      $type === 'daily' &&
      css`
        flex-shrink: 0;
        gap: 1rem;
        min-width: 6rem;
        place-self: center;
      `
    }

    ${
      $type === 'compact' &&
      css`
        flex: 1;
        flex-shrink: 0;
        place-self: unset;
      `
    }
  `}
`;

export const Title = styled.p<{ $type: Props['type'] }>`
  ${({ theme: { colors, fontSizes, fontWeights }, $type }) => css`
    color: ${colors.brandDark};
    display: -webkit-box;
    font-size: ${fontSizes.s12};
    font-weight: ${fontWeights.medium};
    line-height: 1rem;
    margin: 0;
    margin-top: 0.25rem;
    min-height: 0.875rem;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    word-break: break-all;
    width: 100%;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;

    ${
      $type === 'daily' &&
      css`
        flex: 1;
        font-size: ${fontSizes.s14};
        font-weight: ${fontWeights.regular};
        margin-top: 0;

        @media print {
          word-break: break-word;
          -webkit-line-clamp: unset;
        }
      `
    }
  `}
`;

export const FooterArea = styled.div<{ $type: Props['type'] }>`
  ${({ $type }) => css`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    margin-top: 0.5rem;
    width: 100%;
    z-index: 1;

    ${
      $type === 'daily' &&
      css`
        gap: 0.5rem;
        justify-content: flex-end;
        margin: 0;
        margin-left: 0.5rem;
        width: auto;
      `
    }

    ${
      $type === 'compact' &&
      css`
        width: auto;
        margin: 0;
      `
    }
  `}
`;

export const Avatar = styled.div<{ $color: string; $isDark: boolean }>`
  ${({ theme: { colors, fontWeights }, $color, $isDark }) => css`
    align-items: center;
    background: ${$color} !important;
    border-radius: 1.25rem;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    height: 1.25rem;
    width: 1.25rem;
    position: relative;

    &::before {
      border-radius: inherit;
      box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20} !important;
      content: '';
      display: block;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      user-select: none;
      width: 100%;
      z-index: 1;
    }

    span {
      color: ${$isDark ? colors.brandLightAlpha90 : colors.brandDarkAlpha80} !important;
      display: inline-block;
      font-size: 0.625rem;
      font-weight: ${fontWeights.semiBold};
      line-height: 1em;
    }

    @media print {
      && {
        background: ${$color} !important;
        box-shadow: none !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
        -webkit-print-color-adjust: exact !important;
      }

      &::before {
        box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20} !important;
      }

      span {
        color: ${$isDark ? colors.brandLightAlpha90 : colors.brandDarkAlpha80} !important;
      }
    }
  `}
`;

export const SocialArea = styled.div<{ $type: Props['type'] }>`
  ${({ theme: { colors, fontSizes }, $type }) => css`
    p {
      color: ${colors.brandDarkTint30};
      font-size: ${fontSizes.s12};
      display: -webkit-box;
      margin: 0;
      overflow: hidden;
      text-align: right;
      text-overflow: ellipsis;
      width: 100%;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    div {
      align-items: center;
      display: flex;
      flex: 1;
      flex-shrink: 0;
      gap: 0.125rem;
      height: 1.25rem;
      width: 100%;

      .icon {
        height: 0.875rem;
        width: 0.875rem;
      }

      span {
        color: ${colors.brandDarkTint30};
        font-size: ${fontSizes.s12};
        line-height: 1em;
      }
    }

    ${
      $type === 'daily' &&
      css`
        order: -1;
      `
    }
  `}
`;

export const ProfileList = styled.div`
  ${({ theme: { borderRadius, colors } }) => css`
    background: ${colors.brandLight};
    border-radius: ${borderRadius.sm};
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    user-select: none;

    ul {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      overflow-y: auto;
      padding: 0.75rem 1rem;
      margin: 0;
      max-height: 7.5rem;
      width: 10rem;
      list-style: none;

      li {
        align-items: center;
        display: flex;
        gap: 0.25rem;

        .icon {
          flex-shrink: 0;
          height: 0.875rem;
          width: 0.875rem;
        }

        .text {
          word-break: break-all;
        }
      }

      &::-webkit-scrollbar {
        height: 8px;
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: white;
      }

      &::-webkit-scrollbar-thumb {
        background-clip: padding-box;
        background-color: ${colors.brandDarkTint80};
        border-radius: 8px;
        height: 8px;
        -webkit-border-radius: 8px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.brandDarkTint70};
      }
    }
  `}
`;
